import {useAuthStore} from "@/store/auth";
import router from "@/router";
import {query} from "@/graphql/client";
import {gql} from "@urql/vue";
import {isValidURL, parseURL} from "@/helper/url";
import {App as CapacitorApp, URLOpenListenerEvent} from "@capacitor/app";
import {alertController} from "@ionic/vue";
import useEmitter from "./emitter";
import {useAppState} from "./appState";
import {useEmployeeStore} from "@/store/employee";
import {useCandidateStore} from "@/store/candidate";
import {presentScriptedMessage, resetConversation} from "@/helper/chat/chatBL";
import {scriptedMessages} from "@/helper/chat/scriptedMessage";
import {useChatStore} from "@/helper/chat/chatStore";
import {fetchWithAuth, fetchWithRetry} from "@/client";
import {jobsiteFragment} from "@/helper/jobsite";
import {graphql} from "@/graphql/generated";

let _isLoggingIn = false
export function initAuth() {
    const emitter = useEmitter()

    // attach universal link listener
    CapacitorApp.addListener('appUrlOpen', async function (event: URLOpenListenerEvent) {
        _isLoggingIn = true

        //https://capacitorjs.com/docs/guides/deep-links#vue
        console.log('[CapacitorApp.addListener] App opened with URL', event);

        // get magic link
        const universalLink = event.url

        // try to login with it (exception & alert otherwise)
        try {
            await loginWithUniversalLink(universalLink, false)
        } catch (e:any) {
            const alert = await alertController.create({
                header: 'Fehler',
                message: e?.message || 'Unbekannter Fehler aufgetreten',
                buttons: ['Ok'],
            });

            await alert.present();
        } finally {
            _isLoggingIn = false
        }
    });

    // setup login & logout events
    // emitter.on('auth:token:update', ({newToken, oldToken}) => {
    //     if (newToken && !oldToken) {
    //         emitter.emit('auth:login')
    //     } else if (!newToken && oldToken) {
    //         emitter.emit('auth:logout')
    //     }
    // })

    // attach logout listener to redirect
    // emitter.on('auth:logout', () => {
    //     resetRouterBasedOnAuth()
    // })
}

export function isLoggingIn() {
    return _isLoggingIn
}

export function isLoggedIn() {
    const authStore = useAuthStore()
    return authStore.hasToken()
}

function parseUniversalLink(universalLink: string) {
    const url = parseURL(universalLink)
    return url?.search
}

const isValidUniversalLink = (universalLink: string) => isValidURL(universalLink)

export const fuzzyLinkParsing = (textWithPotenialLink: string): string=>{
    const start = textWithPotenialLink.indexOf(import.meta.env.VITE_IBFG_UNIVERSAL_LINK)

    if(start === -1) {return ""}

    textWithPotenialLink = textWithPotenialLink.substring(start)

    const end = textWithPotenialLink.indexOf(' ')
    if (end != -1) {
        textWithPotenialLink = textWithPotenialLink.substring(0, end)
    }
    return textWithPotenialLink
}

async function universalLinkToJWT(universalLink: string, tryFuzzyLinkParsing:boolean) {

    // try to find the link in a bunch of text
    if (tryFuzzyLinkParsing) {
        universalLink = fuzzyLinkParsing(universalLink)
    }

    if (!isValidUniversalLink(universalLink)) {
        throw new Error("Ungültiger Einladungslink")
    }

    const universalLinkComponents = parseUniversalLink(universalLink)
    if (!universalLinkComponents) {
        throw new Error('Ungültiger Einladungslink')
    }

    // replace universalLink baseURL with backend API
    const baseURL = import.meta.env.VITE_LOGIN_ENDPOINT as string
    const loginURL = baseURL + universalLinkComponents

    const loginResponse = await fetchWithRetry(loginURL);
    if (!loginResponse.ok) {
        if (loginResponse.status === 410) {
            throw new Error('Der Einladungslink ist bereits abgelaufen.')
        } else {
            throw new Error('Anmeldung fehlgeschlagen: ' + loginResponse.status)
        }
    }

    const loginResponseJson = await loginResponse.json();

    return loginResponseJson.token;
}

export async function loginWithUniversalLink(
    universalLink: string,
    tryFuzzyLinkParsing: boolean
) {

    const token = await universalLinkToJWT(universalLink, tryFuzzyLinkParsing)

    await useAppState().login(token).then(async () => {
        if (!useAuthStore().isUserRole('consultant')) {
            await resetConversation()
        }

        if(useAuthStore().isUserRole('employee')) {
            presentScriptedMessage(scriptedMessages.afterSwitchToEmployee)
        }
    })
}

export const switchToEmployee = () => {
    fetchWithAuth(import.meta.env.VITE_CANDIDATE_EMPLOYEE_SWITCH_ENDPOINT, null, "POST").then(
        async (response) => {
            if (response.status === 200) {
                response.json().then(responseJson => {
                    if(responseJson.token) {
                        useAppState().login(responseJson.token).then(() => {
                            resetConversation().then(() => {
                                presentScriptedMessage(scriptedMessages.afterSwitchToEmployee)
                            })
                        })
                    }else {
                        throw new Error('No token found in response')
                    }
                })
            }else{
                throw new Error('Unexpected response status: ' + response.status)
            }
        }
    )
}

export const userFragment = graphql(`fragment UserFragment on User {
    id,
    userName,
    readableUserIdentifier,
    roles
}`)


export const fetchMyUser = async ({useImpersonation} : {useImpersonation: boolean}) => {
    const authStore = useAuthStore();

    const result = await query(graphql(`
        query GetMyUser {
            user {
                ...UserFragment
            }
        }
    `), {}, {useImpersonation});

    const user = result?.data?.user;

    if (!user) {
        return;
    }
    return user;
};

// export const resetRouterBasedOnAuth = () => {
//     router.push(defaultRouteBasedOnAuth())
// };

export const defaultRouteBasedOnAuth = () => {
    const authStore = useAuthStore();
    switch (true) {
        case !isLoggedIn():
            return "/auth";
        case authStore.isUserRole('consultant') && !authStore.isImpersonating():
            return  "/employees";
        case (authStore.isUserRole('consultant') && authStore.isImpersonating()) || authStore.isUserRole('employee'):
            return "/chat";
        case authStore.isUserRole('candidate'):
            return "/public";
        default:
            throw new Error('unknown auth config - no target for redirecting')
    }
}

export const relogByChatInput = async (message:string):Promise<void> => {
    useChatStore().setGlobalLoaderState(true, 'Anmeldung...')

    try {
        await loginWithUniversalLink(message, true)

        if (useAuthStore().isUserRole('employee')) {
            await useEmployeeStore().reloadEmployee()
        }
        if (useAuthStore().isUserRole('candidate')) {
            await useCandidateStore().reloadCandidate()
        }
    } finally {
        useChatStore().setGlobalLoaderState(false)
    }
}
